import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../context/FilterContext';

import { useYearlyTrendQuery, useKpiMonthlyQuery, useKpiComparisonQuery, useDailyChartQuery, useQuarterlyTrendQuery, useMonthlyTrendWithCampaignsQuery, useDailyChartWithCampaignsQuery } from '../../hooks/queries/useDashboardQuery';
import './MonthlyOpenSalesChart.css';
import '../charts/ChartStyles.css';


type ViewMode = 'monthly' | 'yearly' | 'daily' | 'comparison' | 'quarterly';

interface ChartData {
  monthly: any[];
  yearly: any[];
  quarterly: any[];
  daily: any[];
  comparison: any;
}


const formatMonthForDisplay = (yearMonth: string) => {
  const [year, month] = yearMonth.split('-');
  return `${year}/${month}`;
};

const formatMonthForIphone = (yearMonth: string) => {
  const [year, month] = yearMonth.split('-');
  return `${year.slice(2)}/${parseInt(month)}`;
};

const formatQuarterForDisplay = (yearQuarter: string) => {
  
  return yearQuarter.replace('-', ' ');
};

const getMonthDateRange = (yearMonth: string): { startDate: string; endDate: string } => {
  const [year, month] = yearMonth.split('-');
  const startDate = `${year}-${month}-01`;
  
  const yearNum = parseInt(year, 10);
  const monthNum = parseInt(month, 10);
  const lastDay = new Date(yearNum, monthNum, 0).getDate();
  
  const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
  
  return { startDate, endDate };
};

const getSalesforceIdFromUrl = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('storeId') || 'a0JPC00000cKHlB2AW';
};


const isIPhone = () => {
  return /iPhone/i.test(navigator.userAgent) && window.innerWidth <= 480;
};

const MonthlyOpenSalesChart: React.FC = () => {
  const { t } = useTranslation();
  const salesforceId = getSalesforceIdFromUrl();
  const { isFiltersApplying } = useFilters();

  const isLaptop = window.innerWidth <= 1600;

  
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [selectedQuarter, setSelectedQuarter] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(isIPhone() ? 'quarterly' : 'monthly');
  
  
  const { data: monthlyDataWithCampaigns, isLoading: campaignLoading } = useMonthlyTrendWithCampaignsQuery(
    salesforceId,
    {},
    !!salesforceId
  );
  
  
  const { data: monthlyData, isLoading: monthlyLoading, error: monthlyError } = useKpiMonthlyQuery({
    storeId: salesforceId,
    filters: {},
    enabled: !!salesforceId
  });
  
  const { data: yearlyData, isLoading: yearlyLoading, error: yearlyError } = useYearlyTrendQuery(
    salesforceId,
    !!salesforceId && viewMode === 'yearly'
  );
  
  const { data: quarterlyData, isLoading: quarterlyLoading, error: quarterlyError } = useQuarterlyTrendQuery(
    salesforceId,
    !!salesforceId && (viewMode === 'quarterly' || isIPhone())
  );
  
  const { data: comparisonData, isLoading: comparisonLoading, error: comparisonError } = useKpiComparisonQuery({
    storeId: salesforceId,
    filters: {},
    enabled: !!salesforceId && viewMode === 'comparison'
  });
  
  const { data: dailyData, isLoading: dailyLoading, error: dailyError } = useDailyChartQuery(
    salesforceId,
    selectedMonth || '',
    !!salesforceId && !!selectedMonth && viewMode === 'daily'
  );

  
  const { data: dailyDataWithCampaigns, isLoading: dailyCampaignLoading } = useDailyChartWithCampaignsQuery(
    salesforceId,
    selectedMonth || '',
    !!salesforceId && !!selectedMonth && viewMode === 'daily'
  );

  
  const chartData = useMemo((): ChartData => {
    
    let baseMonthlyData = monthlyData || [];
    if (viewMode === 'monthly' && monthlyDataWithCampaigns?.monthlyData) {
      baseMonthlyData = monthlyDataWithCampaigns.monthlyData;
    }
    
    
    let filteredMonthlyData = baseMonthlyData;
    if (isIPhone() && selectedQuarter && baseMonthlyData) {
      const [year, quarter] = selectedQuarter.split('-Q');
      const quarterNum = parseInt(quarter);
      const startMonth = (quarterNum - 1) * 3 + 1;
      const endMonth = quarterNum * 3;
      
      filteredMonthlyData = baseMonthlyData.filter((item: any) => {
        const [itemYear, itemMonth] = item.year_month.split('-');
        const monthNum = parseInt(itemMonth);
        return itemYear === year && monthNum >= startMonth && monthNum <= endMonth;
      });
    }
    
    
    let finalDailyData = dailyData || [];
    if (viewMode === 'daily' && dailyDataWithCampaigns?.dailyData) {
      finalDailyData = dailyDataWithCampaigns.dailyData;
    }

    return {
      monthly: filteredMonthlyData,
      yearly: yearlyData || [],
      quarterly: quarterlyData || [],
      daily: finalDailyData,
      comparison: comparisonData || {}
    };
  }, [monthlyData, monthlyDataWithCampaigns, yearlyData, quarterlyData, dailyData, dailyDataWithCampaigns, comparisonData, selectedQuarter, viewMode]);
  
  
  const loading = useMemo(() => {
    switch (viewMode) {
      case 'monthly': return monthlyLoading || campaignLoading;
      case 'yearly': return yearlyLoading;
      case 'quarterly': return quarterlyLoading;
      case 'daily': return dailyLoading || dailyCampaignLoading;
      case 'comparison': return comparisonLoading;
      default: return monthlyLoading || campaignLoading;
    }
  }, [viewMode, monthlyLoading, campaignLoading, yearlyLoading, quarterlyLoading, dailyLoading, dailyCampaignLoading, comparisonLoading]);
  
  
  const error = useMemo(() => {
    const currentError = (() => {
      switch (viewMode) {
        case 'monthly': return monthlyError;
        case 'yearly': return yearlyError;
        case 'quarterly': return quarterlyError;
        case 'daily': return dailyError;
        case 'comparison': return comparisonError;
        default: return monthlyError;
      }
    })();
    
    if (currentError) {
      return currentError instanceof Error ? currentError.message : t('monthlyChart.loadingFailed');
    }
    return null;
  }, [viewMode, monthlyError, yearlyError, dailyError, comparisonError, t]);

  
  const monthlyOption = useMemo(() => {
    const monthlyData = chartData.monthly;
    
    if (monthlyData.length === 0) {
      return {
        grid: {
          left: '3%',
          right: '4%',
          bottom: '5%',
          top: '60px',
          containLabel: true
        }
      };
    }

    const months = monthlyData.map((item: any) => 
      window.innerWidth <= 480 ? formatMonthForDisplay(item.year_month) : formatMonthForDisplay(item.year_month)
    );
    
    const netSales = monthlyData.map((item: any) => {
      if (typeof item.net_sales === 'string') {
        
        return parseFloat(item.net_sales.replace(/[$,]/g, '')) || 0;
      }
      return Number(item.net_sales) || 0;
    });
    
    const orderCounts = monthlyData.map((item: any) => {
      if (typeof item.order_count === 'string') {
        return parseInt(item.order_count.replace(/,/g, '')) || 0;
      }
      return Number(item.order_count) || 0;
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999'
          }
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333',
          fontSize: isLaptop ? 12 : 13
        },
        confine: true, 
        formatter: function(params: any) {
          const dataIndex = params[0].dataIndex;
          const monthItem = monthlyData[dataIndex];
          const yearMonth = monthItem.year_month;
          const [year, month] = yearMonth.split('-');
          const fullDateStr = t('monthlyChart.month', { year, month });
          
          const netSalesValue = params[0].value;
          const orderCountValue = params[1].value;
          
          
          const campaigns = monthItem.campaigns || [];
          const hasCampaigns = campaigns.length > 0;
          
          let campaignSection = '';
          if (hasCampaigns) {
            campaignSection = `
              <div style="border-top: 1px solid #eee; margin-top: ${isLaptop ? '8px' : '10px'}; padding-top: ${isLaptop ? '6px' : '8px'};">
                <div style="font-weight: bold; margin-bottom: ${isLaptop ? '4px' : '6px'}; color: #F97F76; font-size: ${isLaptop ? '12px' : '13px'};">
                  🎯 Active Campaigns (${campaigns.length})
                </div>
                ${campaigns.map((campaign: any) => `
                  <div style="margin-bottom: ${isLaptop ? '3px' : '4px'}; font-size: ${isLaptop ? '10px' : '11px'};">
                    <strong>${campaign.platform}:</strong> ${campaign.campaign_name}
                  </div>
                `).join('')}
              </div>
            `;
          }
          
          return `<div style="padding: 3px;">
            <div style="font-weight: bold; margin-bottom: ${isLaptop ? '6px' : '8px'}; font-size: ${isLaptop ? '13px' : '14px'};">${fullDateStr}</div>
            <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
              <span style="margin-right: 15px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 10px; height: 10px; background-color: #F97F76; margin-right: 5px; border-radius: 50%;"></span>
                <strong>${t('monthlyChart.sales')}:</strong>
              </span>
              <span style="font-weight: bold;">$${netSalesValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="margin-right: 15px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 10px; height: 10px; background-color: #474646; margin-right: 5px; border-radius: 50%;"></span>
                <strong>${t('monthlyChart.orders')}:</strong>
              </span>
              <span style="font-weight: bold;">${orderCountValue.toLocaleString()}</span>
            </div>
            ${campaignSection}
            <div style="margin-top: ${isLaptop ? '6px' : '8px'}; font-size: ${isLaptop ? '11px' : '12px'}; color: #666; font-style: italic;">
              ${t('monthlyChart.clickToViewDaily')}
            </div>
          </div>`;
        }
      },
      legend: {
        data: [t('monthlyChart.sales'), t('monthlyChart.orders')],
        top: 0,
        textStyle: {
          color: '#333',
          fontSize: isLaptop ? 12 : 13,
          fontWeight: 'bold'
        },
        itemGap: isLaptop ? 25 : 30,
        bottom: 0
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '5%',
        top: isLaptop ? '50px' : '60px',
        containLabel: true
      },
      xAxis: [
        {
          type: 'category',
          data: months,
          axisPointer: {
            type: 'shadow'
          },
          axisLabel: {
            rotate: 45,
            interval: 0,
            fontSize: window.innerWidth <= 480 ? 10 : (isLaptop ? 11 : 12),
            color: '#333',
            fontWeight: 'bold',
            ...(window.innerWidth <= 480 && {
              margin: 8,
              align: 'right'
            })
          },
          axisTick: {
            alignWithLabel: true
          },
          axisLine: {
            lineStyle: {
              width: isLaptop ? 1.5 : 2
            }
          }
        }
      ],
      yAxis: [
        {
          type: 'value',
          name: t('monthlyChart.sales'),
          min: 0,
          axisLabel: {
            formatter: '${value}',
            color: '#333',
            fontSize: isLaptop ? 11 : 12,
            fontWeight: 'bold'
          },
          nameTextStyle: {
            color: '#333',
            fontSize: isLaptop ? 12 : 13,
            fontWeight: 'bold',
            padding: [0, 0, 0, isLaptop ? -25 : -30]
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#F97F76',
              width: isLaptop ? 1.5 : 2
            }
          },
          splitLine: {
            lineStyle: {
              opacity: 0.3
            }
          }
        },
        {
          type: 'value',
          name: t('monthlyChart.orders'),
          min: 0,
          position: 'right',
          axisLabel: {
            color: '#333',
            fontSize: isLaptop ? 11 : 12,
            fontWeight: 'bold'
          },
          nameTextStyle: {
            color: '#333',
            fontSize: isLaptop ? 12 : 13,
            fontWeight: 'bold',
            padding: [0, isLaptop ? -25 : -30, 0, 0]
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#474646',
              width: isLaptop ? 1.5 : 2
            }
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: [
        {
          name: t('monthlyChart.sales'),
          type: 'bar',
          data: netSales,
          itemStyle: {
            color: '#F97F76'  
          },
          emphasis: {
            itemStyle: {
              color: '#F52314'
            }
          },
          barMaxWidth: isLaptop ? 35 : 40
        },
        {
          name: t('monthlyChart.orders'),
          type: 'line',
          yAxisIndex: 1,
          data: orderCounts,
          symbol: 'circle',
          symbolSize: isLaptop ? 6 : 7,
          itemStyle: {
            color: '#474646'
          },
          lineStyle: {
            width: isLaptop ? 2.5 : 3,
            color: '#474646'
          },
          emphasis: {
            itemStyle: {
              color: '#F52314'
            }
          }
        }
      ],
      
      toolbox: {
        show: false
      },
      dataZoom: [],
      brush: {
        toolbox: []
      }
    };
  }, [chartData.monthly, t, isLaptop]);

  const quarterlyOption = useMemo(() => {
    const quarterlyData = chartData.quarterly;
    
    if (quarterlyData.length === 0) {
      return {
        grid: {
          left: '3%',
          right: '4%',
          bottom: '5%',
          top: '60px',
          containLabel: true
        }
      };
    }

    const quarters = quarterlyData.map((item: any) => formatQuarterForDisplay(item.year_quarter));
    
    const netSales = quarterlyData.map((item: any) => {
      if (typeof item.net_sales === 'string') {
        return parseFloat(item.net_sales.replace(/[$,]/g, '')) || 0;
      }
      return Number(item.net_sales) || 0;
    });
    
    const orderCounts = quarterlyData.map((item: any) => {
      if (typeof item.order_count === 'string') {
        return parseInt(item.order_count.replace(/,/g, '')) || 0;
      }
      return Number(item.order_count) || 0;
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999'
          }
        },
        formatter: function (params: any) {
          const monthInfo = params[0];
          const orderInfo = params[1];
          
          const fullQuarterStr = monthInfo.name;
          const netSalesValue = monthInfo.value || 0;
          const orderCountValue = orderInfo ? orderInfo.value : 0;

          return `<div style="padding: 3px;">
            <div style="font-weight: bold; margin-bottom: 8px; font-size: 14px;">${fullQuarterStr}</div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
              <span style="margin-right: 15px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 10px; height: 10px; background-color: #F97F76; margin-right: 5px; border-radius: 50%;"></span>
                <strong>${t('monthlyChart.sales')}:</strong>
              </span>
              <span style="font-weight: bold;">$${netSalesValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="margin-right: 15px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 10px; height: 10px; background-color: #474646; margin-right: 5px; border-radius: 50%;"></span>
                <strong>${t('monthlyChart.orders')}:</strong>
              </span>
              <span style="font-weight: bold;">${orderCountValue.toLocaleString()}</span>
            </div>
            <div style="margin-top: 8px; font-size: 12px; color: #666; font-style: italic;">
              ${t('monthlyChart.clickToViewDaily')}
            </div>
          </div>`;
        }
      },
      legend: {
        data: [t('monthlyChart.sales'), t('monthlyChart.orders')],
        top: 0,
        textStyle: {
          color: '#333',
          fontSize: 13,
          fontWeight: 'bold'
        },
        itemGap: 30
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '5%',
        top: '60px',
        containLabel: true
      },
      xAxis: [
        {
          type: 'category',
          data: quarters,
          axisPointer: {
            type: 'shadow'
          },
          axisLabel: {
            rotate: window.innerWidth <= 480 ? 45 : 0, 
            interval: 0,
            fontSize: window.innerWidth <= 480 ? 10 : 12,
            color: '#333',
            fontWeight: 'bold',
            ...(window.innerWidth <= 480 && {
              margin: 8, 
              align: 'right'
            })
          },
          axisTick: {
            alignWithLabel: true
          },
          axisLine: {
            lineStyle: {
              width: 2
            }
          }
        }
      ],
      yAxis: [
        {
          type: 'value',
          name: t('monthlyChart.sales'),
          min: 0,
          axisLabel: {
            formatter: '${value}',
            color: '#333',
            fontSize: 12,
            fontWeight: 'bold'
          },
          nameTextStyle: {
            color: '#333',
            fontSize: 13,
            fontWeight: 'bold',
            padding: [0, 0, 0, -30]
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#F97F76',
              width: 2
            }
          },
          splitLine: {
            lineStyle: {
              opacity: 0.3
            }
          }
        },
        {
          type: 'value',
          name: t('monthlyChart.orders'),
          min: 0,
          axisLabel: {
            formatter: '{value}',
            color: '#333',
            fontSize: 12,
            fontWeight: 'bold'
          },
          nameTextStyle: {
            color: '#333',
            fontSize: 13,
            fontWeight: 'bold',
            padding: [0, -30, 0, 0]
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#474646',
              width: 2
            }
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: [
        {
          name: t('monthlyChart.sales'),
          type: 'bar',
          tooltip: {
            valueFormatter: function (value: any) {
              return '$' + Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }
          },
          data: netSales,
          itemStyle: {
            color: '#F97F76',
            borderRadius: [4, 4, 0, 0]
          }
        },
        {
          name: t('monthlyChart.orders'),
          type: 'line',
          yAxisIndex: 1,
          tooltip: {
            valueFormatter: function (value: any) {
              return Number(value).toLocaleString();
            }
          },
          data: orderCounts,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            color: '#474646'
          },
          lineStyle: {
            width: 3,
            color: '#474646'
          }
        }
      ]
    };
  }, [chartData.quarterly, t]);

  const yearlyOption = useMemo(() => {
    const yearlyData = chartData.yearly;
    
    if (yearlyData.length === 0) {
      return {
        grid: {
          left: '3%',
          right: '4%',
          bottom: '5%',
          top: '60px',
          containLabel: true
        }
      };
    }

    const years = yearlyData.map((item: any) => item.year.toString());
    
    const netSales = yearlyData.map((item: any) => {
      if (typeof item.net_sales === 'string') {
        
        return parseFloat(item.net_sales.replace(/[$,]/g, '')) || 0;
      }
      return Number(item.net_sales) || 0;
    });
    
    const orderCounts = yearlyData.map((item: any) => {
      if (typeof item.order_count === 'string') {
        return parseInt(item.order_count.replace(/,/g, '')) || 0;
      }
      return Number(item.order_count) || 0;
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999'
          }
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333',
          fontSize: isLaptop ? 12 : 13
        },
        formatter: function(params: any) {
          const yearValue = yearlyData[params[0].dataIndex].year;
          const fullDateStr = t('monthlyChart.year', { year: yearValue });
          
          const netSalesValue = params[0].value;
          const orderCountValue = params[1].value;
          
          return `<div style="padding: 3px;">
            <div style="font-weight: bold; margin-bottom: ${isLaptop ? '6px' : '8px'}; font-size: ${isLaptop ? '13px' : '14px'};">${fullDateStr}</div>
            <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
              <span style="margin-right: 15px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 10px; height: 10px; background-color: #F97F76; margin-right: 5px; border-radius: 50%;"></span>
                <strong>${t('monthlyChart.yearlySales')}:</strong>
              </span>
              <span style="font-weight: bold;">$${netSalesValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="margin-right: 15px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 10px; height: 10px; background-color: #474646; margin-right: 5px; border-radius: 50%;"></span>
                <strong>${t('monthlyChart.yearlyOrders')}:</strong>
              </span>
              <span style="font-weight: bold;">${orderCountValue.toLocaleString()}</span>
            </div>
          </div>`;
        }
      },
      legend: {
        data: [t('monthlyChart.yearlySales'), t('monthlyChart.yearlyOrders')],
        top: 0,
        textStyle: {
          color: '#333',
          fontSize: isLaptop ? 12 : 13,
          fontWeight: 'bold'
        },
        itemGap: isLaptop ? 25 : 30,
        bottom: 0
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '5%',
        top: isLaptop ? '50px' : '60px',
        containLabel: true
      },
      xAxis: [
        {
          type: 'category',
          data: years,
          axisPointer: {
            type: 'shadow'
          },
          axisLabel: {
            interval: 0,
            fontSize: isLaptop ? 11 : 12,
            color: '#333',
            fontWeight: 'bold'
          },
          axisLine: {
            lineStyle: {
              width: isLaptop ? 1.5 : 2
            }
          }
        }
      ],
      yAxis: [
        {
          type: 'value',
          name: t('monthlyChart.yearlySales'),
          min: 0,
          axisLabel: {
            formatter: '${value}',
            color: '#333',
            fontSize: isLaptop ? 11 : 12,
            fontWeight: 'bold'
          },
          nameTextStyle: {
            color: '#333',
            fontSize: isLaptop ? 12 : 13,
            fontWeight: 'bold',
            padding: [0, 0, 0, isLaptop ? -25 : -30]
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#F97F76',
              width: isLaptop ? 1.5 : 2
            }
          },
          splitLine: {
            lineStyle: {
              opacity: 0.3
            }
          }
        },
        {
          type: 'value',
          name: t('monthlyChart.yearlyOrders'),
          min: 0,
          position: 'right',
          axisLabel: {
            color: '#333',
            fontSize: isLaptop ? 11 : 12,
            fontWeight: 'bold'
          },
          nameTextStyle: {
            color: '#333',
            fontSize: isLaptop ? 12 : 13,
            fontWeight: 'bold',
            padding: [0, isLaptop ? -25 : -30, 0, 0]
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#474646',
              width: isLaptop ? 1.5 : 2
            }
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: [
        {
          name: t('monthlyChart.yearlySales'),
          type: 'bar',
          data: netSales,
          itemStyle: {
            color: '#F97F76'
          },
          emphasis: {
            itemStyle: {
              color: '#F52314'
            }
          },
          barMaxWidth: isLaptop ? 50 : 60
        },
        {
          name: t('monthlyChart.yearlyOrders'),
          type: 'line',
          yAxisIndex: 1,
          data: orderCounts,
          symbol: 'circle',
          symbolSize: isLaptop ? 7 : 8,
          itemStyle: {
            color: '#474646'
          },
          lineStyle: {
            width: isLaptop ? 3 : 4,
            color: '#474646'
          },
          emphasis: {
            itemStyle: {
              color: '#F52314'
            }
          }
        }
      ]
    };
  }, [chartData.yearly, t, isLaptop]);

  const comparisonOption = useMemo(() => {
    const comparisonData = chartData.comparison;
    
    if (!comparisonData || Object.keys(comparisonData).length === 0) {
      return {
        grid: {
          left: '3%',
          right: '4%',
          bottom: '5%',
          top: '60px',
          containLabel: true
        }
      };
    }

    const months = [
      t('months.jan'), t('months.feb'), t('months.mar'), t('months.apr'), 
      t('months.may'), t('months.jun'), t('months.jul'), t('months.aug'), 
      t('months.sep'), t('months.oct'), t('months.nov'), t('months.dec')
    ];
    
    
    const allYears = new Set<number>();
    Object.values(comparisonData).forEach((monthData: any) => {
      monthData.forEach((yearData: any) => {
        allYears.add(yearData.year);
      });
    });
    const sortedYears = Array.from(allYears).sort();
    
    
    const yearColors = [
      '#474646', '#F97F76', '#79ADDC', '#F9C27B', '#9B59B6',
      '#1ABC9C', '#E74C3C', '#3498DB', '#F39C12', '#27AE60'
    ];
    
    
    const orderLineColors = [
      '#27AE60', '#E67E22', '#8E44AD', '#D35400', '#2C3E50',
      '#16A085', '#C0392B', '#2980B9', '#E74C3C', '#8B4513'
    ];

    
    const salesSeries = sortedYears.map((year, index) => {
      const data = months.map((month, monthIndex) => {
        const monthNumber = monthIndex + 1;
        const monthData = comparisonData[monthNumber];
        if (monthData) {
          const yearData = monthData.find((item: any) => item.year === year);
          return yearData ? yearData.net_sales : 0;
        }
        return 0;
      });

      return {
        name: t('kpi.year', { year }), 
        type: 'bar',
        data: data,
        itemStyle: {
          color: yearColors[index % yearColors.length]
        },
        emphasis: {
          itemStyle: {
            color: yearColors[index % yearColors.length],
            shadowBlur: 10,
            shadowColor: 'rgba(0,0,0,0.3)'
          }
        },
        barMaxWidth: isLaptop ? 25 : 30,
        legendHoverLink: false 
      };
    });

    
    const orderSeries = sortedYears.map((year, index) => {
      const data = months.map((month, monthIndex) => {
        const monthNumber = monthIndex + 1;
        const monthData = comparisonData[monthNumber];
        if (monthData) {
          const yearData = monthData.find((item: any) => item.year === year);
          return yearData ? yearData.order_count : 0;
        }
        return 0;
      });

      const lineColor = yearColors[index % yearColors.length]; 

      return {
        name: t('kpi.year', { year }), 
        type: 'line',
        yAxisIndex: 1,
        data: data,
        symbol: 'circle',
        symbolSize: isLaptop ? 6 : 7,
        itemStyle: {
          color: lineColor
        },
        lineStyle: {
          width: isLaptop ? 2.5 : 3,
          color: lineColor
        },
        emphasis: {
          itemStyle: {
            color: lineColor,
            shadowBlur: 10
          }
        },
        legendHoverLink: false 
      };
    });

    const allSeries = [...salesSeries, ...orderSeries];
    
    const legendData = sortedYears.map(year => t('kpi.year', { year }));

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999'
          }
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333',
          fontSize: isLaptop ? 12 : 13
        },
        confine: true, 
        formatter: function(params: any) {
          const monthStr = params[0].axisValue;
          let html = `<div style="padding: 3px;">
            <div style="font-weight: bold; margin-bottom: ${isLaptop ? '6px' : '8px'}; font-size: ${isLaptop ? '13px' : '14px'};">${t('monthlyChart.comparisonTooltip', { month: monthStr })}</div>`;
          
          
          const yearGroups: { [key: number]: { sales: any, orders: any } } = {};
          
          params.forEach((param: any, index: number) => {
            const year = sortedYears[index < sortedYears.length ? index : index - sortedYears.length];
            const isSales = index < sortedYears.length;
            
            if (!yearGroups[year]) {
              yearGroups[year] = { sales: null, orders: null };
            }
            
            if (isSales) {
              yearGroups[year].sales = param;
            } else {
              yearGroups[year].orders = param;
            }
          });
          
          
          sortedYears.forEach((year, yearIndex) => {
            const group = yearGroups[year];
            if (!group) return;
            
            
            html += `<div style="margin-top: ${yearIndex > 0 ? '8px' : '4px'}; margin-bottom: 4px;">
              <div style="font-weight: bold; font-size: ${isLaptop ? '12px' : '13px'}; color: #666; border-bottom: 1px solid #eee; padding-bottom: 2px;">
                📊 ${t('kpi.year', { year })}
              </div>
            </div>`;
            
            
            if (group.sales) {
              const salesValue = `$${group.sales.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              html += `<div style="display: flex; justify-content: space-between; margin-bottom: 3px; margin-left: 8px;">
                <span style="margin-right: 10px; display: inline-flex; align-items: center;">
                  <span style="display: inline-block; width: 8px; height: 8px; background-color: ${group.sales.color}; margin-right: 5px; border-radius: 50%;"></span>
                  <strong>${t('monthlyChart.sales')}:</strong>
                </span>
                <span style="font-weight: bold;">${salesValue}</span>
              </div>`;
            }
            
            
            if (group.orders) {
              const ordersValue = group.orders.value.toLocaleString();
              html += `<div style="display: flex; justify-content: space-between; margin-bottom: 3px; margin-left: 8px;">
                <span style="margin-right: 10px; display: inline-flex; align-items: center;">
                  <span style="display: inline-block; width: 8px; height: 8px; background-color: ${group.orders.color}; margin-right: 5px; border-radius: 50%;"></span>
                  <strong>${t('monthlyChart.orders')}:</strong>
                </span>
                <span style="font-weight: bold;">${ordersValue}</span>
              </div>`;
            }
          });
          
          html += '</div>';
          return html;
        }
      },
      legend: {
        data: legendData,
        top: 0,
        textStyle: {
          color: '#333',
          fontSize: window.innerWidth <= 480 ? 9 : (window.innerWidth <= 1024 ? 10 : (isLaptop ? 11 : 12)),
          fontWeight: 'bold'
        },
        itemGap: window.innerWidth <= 480 ? 8 : (window.innerWidth <= 1024 ? 12 : (isLaptop ? 15 : 20)),
        type: 'plain',
        orient: 'horizontal',
        itemWidth: window.innerWidth <= 480 ? 12 : (window.innerWidth <= 1024 ? 20 : 25),
        itemHeight: window.innerWidth <= 480 ? 12 : (window.innerWidth <= 1024 ? 12 : 14),
        selectedMode: true 
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '8%',
        top: isLaptop ? '80px' : '90px',
        containLabel: true
      },
      xAxis: [{
        type: 'category',
        data: months,
        axisPointer: {
          type: 'shadow'
        },
        axisLabel: {
          interval: 0,
          fontSize: isLaptop ? 11 : 12,
          color: '#333',
          fontWeight: 'bold',
          rotate: window.innerWidth <= 480 ? 45 : 0
        },
        axisLine: {
          lineStyle: {
            width: isLaptop ? 1.5 : 2
          }
        }
      }],
      yAxis: [
        {
          type: 'value',
          name: t('monthlyChart.sales'),
          min: 0,
          axisLabel: {
            formatter: '${value}',
            color: '#333',
            fontSize: isLaptop ? 11 : 12,
            fontWeight: 'bold'
          },
          nameTextStyle: {
            color: '#333',
            fontSize: isLaptop ? 12 : 13,
            fontWeight: 'bold',
            padding: [0, 0, 0, isLaptop ? -25 : -30]
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#474646',
              width: isLaptop ? 1.5 : 2
            }
          },
          splitLine: {
            lineStyle: {
              opacity: 0.3
            }
          }
        },
        {
          type: 'value',
          name: t('monthlyChart.orders'),
          min: 0,
          position: 'right',
          axisLabel: {
            color: '#333',
            fontSize: isLaptop ? 11 : 12,
            fontWeight: 'bold'
          },
          nameTextStyle: {
            color: '#333',
            fontSize: isLaptop ? 12 : 13,
            fontWeight: 'bold',
            padding: [0, isLaptop ? -25 : -30, 0, 0]
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#F97F76',
              width: isLaptop ? 1.5 : 2
            }
          },
          splitLine: {
            show: false
          }
        }
      ],
      graphic: {
        type: 'text',
        left: 'center',
        bottom: 5,
        style: {
          text: t('monthlyChart.comparisonHint'),
          fontSize: isLaptop ? 10 : 11,
          fontWeight: 'normal',
          fill: '#666',
          fontStyle: 'italic'
        }
      },
      series: allSeries
    };
  }, [chartData.comparison, t, isLaptop]);

  const dailyOption = useMemo(() => {
    const dailyData = chartData.daily;
    
    if (dailyData.length === 0) {
      return {};
    }
  
    const dates = dailyData.map((item: any) => {
      const dateOnly = item.business_date.split('T')[0];
      const [year, month, day] = dateOnly.split('-');
      return `${parseInt(month)}/${parseInt(day)}`;
    });
    
    const netSales = dailyData.map((item: any) => {
      if (typeof item.net_sales === 'string') {
        
        return parseFloat(item.net_sales.replace(/[$,]/g, '')) || 0;
      }
      return Number(item.net_sales) || 0;
    });
    
    const orderCounts = dailyData.map((item: any) => {
      if (typeof item.order_count === 'string') {
        return parseInt(item.order_count.replace(/,/g, '')) || 0;
      }
      return Number(item.order_count) || 0;
    });
  
    return {
      grid: {
        top: window.innerWidth <= 480 ? '50px' : (isLaptop ? 50 : 60),
        bottom: '5%',
        left: '3%',
        right: '4%',
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999'
          }
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333',
          fontSize: isLaptop ? 12 : 13
        },
        confine: true, 
        formatter: function(params: any) {
          const dataIndex = params[0].dataIndex;
          const dayItem = dailyData[dataIndex];
          const businessDate = dayItem.business_date;
          const dateOnly = businessDate.split('T')[0];
          const [year, month, day] = dateOnly.split('-');
          const fullDateStr = `${year}/${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
          
          const netSalesValue = params[0].value;
          const orderCountValue = params[1].value;
          
          
          const campaigns = dayItem.campaigns || [];
          const hasCampaigns = campaigns.length > 0;
          
          let campaignSection = '';
          if (hasCampaigns) {
            campaignSection = `
              <div style="border-top: 1px solid #eee; margin-top: ${isLaptop ? '8px' : '10px'}; padding-top: ${isLaptop ? '6px' : '8px'};">
                <div style="font-weight: bold; margin-bottom: ${isLaptop ? '4px' : '6px'}; color: #F9C27B; font-size: ${isLaptop ? '12px' : '13px'};">
                  🎯 Active Campaigns (${campaigns.length})
                </div>
                ${campaigns.map((campaign: any) => {
                  const startDate = new Date(campaign.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const endDate = new Date(campaign.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  return `
                    <div style="margin-bottom: ${isLaptop ? '3px' : '4px'}; font-size: ${isLaptop ? '10px' : '11px'};">
                      <strong>${campaign.platform}:</strong> ${campaign.campaign_name} <span style="color: #999;">(${startDate} - ${endDate})</span>
                    </div>
                  `;
                }).join('')}
              </div>
            `;
          }
          
          return `<div style="padding: 3px;">
            <div style="font-weight: bold; margin-bottom: ${isLaptop ? '6px' : '8px'}; font-size: ${isLaptop ? '13px' : '14px'};">${fullDateStr}</div>
            <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
              <span style="margin-right: 15px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 10px; height: 10px; background-color: ${hasCampaigns ? '#F9C27B' : '#F97F76'}; margin-right: 5px; border-radius: 50%;"></span>
                <strong>${t('monthlyChart.sales')}:</strong>
              </span>
              <span style="font-weight: bold;">$${netSalesValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="margin-right: 15px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 10px; height: 10px; background-color: #474646; margin-right: 5px; border-radius: 50%;"></span>
                <strong>${t('monthlyChart.orders')}:</strong>
              </span>
              <span style="font-weight: bold;">${orderCountValue.toLocaleString()}</span>
            </div>
            ${campaignSection}
          </div>`;
        }
      },
      legend: {
        data: [t('monthlyChart.sales'), t('monthlyChart.orders')],
        top: 0,
        textStyle: {
          color: '#333',
          fontSize: isLaptop ? 12 : 13,
          fontWeight: 'bold'
        },
        itemGap: isLaptop ? 25 : 30
      },
      xAxis: [{
        type: 'category',
        data: dates,
        axisPointer: {
          type: 'shadow'
        },
        axisLabel: {
          rotate: dates.length > 15 ? 45 : 0,
          interval: dates.length > 20 ? 'auto' : 0,
          fontSize: isLaptop ? 11 : 12,
          color: '#333',
          fontWeight: 'bold'
        },
        axisLine: {
          lineStyle: {
            width: isLaptop ? 1.5 : 2
          }
        }
      }],
      yAxis: [
        {
          type: 'value',
          name: t('monthlyChart.sales'),
          min: 0,
          axisLabel: {
            formatter: '${value}',
            color: '#333',
            fontSize: isLaptop ? 11 : 12,
            fontWeight: 'bold'
          },
          nameTextStyle: {
            color: '#333',
            fontSize: isLaptop ? 12 : 13,
            fontWeight: 'bold',
            padding: [0, 0, 0, isLaptop ? -25 : -30]
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#F97F76',
              width: isLaptop ? 1.5 : 2
            }
          },
          splitLine: {
            lineStyle: {
              opacity: 0.3
            }
          }
        },
        {
          type: 'value',
          name: t('monthlyChart.orders'),
          min: 0,
          position: 'right',
          axisLabel: {
            color: '#333',
            fontSize: isLaptop ? 11 : 12,
            fontWeight: 'bold'
          },
          nameTextStyle: {
            color: '#333',
            fontSize: isLaptop ? 12 : 13,
            fontWeight: 'bold',
            padding: [0, isLaptop ? -25 : -30, 0, 0]
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#474646',
              width: isLaptop ? 1.5 : 2
            }
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: [
        {
          name: t('monthlyChart.sales'),
          type: 'bar',
          data: netSales.map((value: number, index: number) => ({
            value,
            itemStyle: {
              color: dailyData[index]?.has_campaigns ? '#F9C27B' : '#F97F76'
            }
          })),
          itemStyle: {
            color: '#F97F76'  
          },
          emphasis: {
            itemStyle: {
              color: '#F52314'
            }
          },
          barMaxWidth: dates.length > 15 ? (isLaptop ? 15 : 18) : (isLaptop ? 30 : 35)
        },
        {
          name: t('monthlyChart.orders'),
          type: 'line',
          yAxisIndex: 1,
          data: orderCounts,
          symbol: 'circle',
          symbolSize: isLaptop ? 6 : 7,
          itemStyle: {
            color: '#474646'
          },
          lineStyle: {
            width: isLaptop ? 2.5 : 3,
            color: '#474646'
          },
          emphasis: {
            itemStyle: {
              color: '#F52314'
            }
          }
        }
      ]
    };
  }, [chartData.daily, t, isLaptop]);

  
  const currentOption = useMemo(() => {
    switch (viewMode) {
      case 'yearly':
        return yearlyOption;
      case 'quarterly':
        return quarterlyOption;
      case 'daily':
        return dailyOption;
      case 'comparison':
        return comparisonOption;
      default:
        return monthlyOption;
    }
  }, [viewMode, yearlyOption, quarterlyOption, dailyOption, comparisonOption, monthlyOption]);

  
  const currentTitle = useMemo(() => {
    switch (viewMode) {
      case 'yearly':
        return t('monthlyChart.yearlyTitle');
      case 'quarterly':
        return t('monthlyChart.quarterlyTitle'); 
      case 'monthly':
        
        if (isIPhone() && selectedQuarter) {
          return t('monthlyChart.quarterlyMonthlyTitle', { quarter: selectedQuarter });
        }
        return t('monthlyChart.title');
      case 'daily':
        return t('monthlyChart.dailyTitle', { month: formatMonthForDisplay(selectedMonth!) });
      case 'comparison':
        return t('monthlyChart.comparisonTitle');
      default:
        return t('monthlyChart.title');
    }
  }, [viewMode, selectedMonth, selectedQuarter, t]);

  
  const handleChartClick = useCallback((params: any) => {
    if (viewMode === 'quarterly' && !isFiltersApplying) {
      
      const quarterlyData = chartData.quarterly;
      if (quarterlyData.length > 0) {
        const clickedQuarter = quarterlyData[params.dataIndex];
        setSelectedQuarter(clickedQuarter.year_quarter);
        setViewMode('monthly');
      }
    } else if (viewMode === 'monthly' && !isFiltersApplying) {
      
      const monthlyData = chartData.monthly;
      if (monthlyData.length > 0) {
        const clickedMonth = monthlyData[params.dataIndex].year_month;
        
        
        setSelectedMonth(clickedMonth);
        setViewMode('daily');
        
      }
    }
  }, [viewMode, isFiltersApplying, chartData.monthly, chartData.quarterly]);

  const handleBackToMonthly = useCallback(() => {
    if (isIPhone() && selectedQuarter) {
      
      setViewMode('quarterly');
      setSelectedMonth(null);
      setSelectedQuarter(null); 
    } else {
      
      setViewMode(isIPhone() ? 'quarterly' : 'monthly');
      setSelectedMonth(null);
      setSelectedQuarter(null);
    }
  }, [selectedQuarter]);

  const handleShowYearlyView = useCallback(() => {
    setViewMode('yearly');
    
  }, []);

  const handleShowComparisonView = useCallback(() => {
    setViewMode('comparison');
    
  }, []);

  
  const chartEvents = useMemo(() => ({
    'click': handleChartClick
  }), [handleChartClick]);

  if (loading || isFiltersApplying) {
    return (
      <div className="monthly-open-sales-chart">
        <div className="monthly-chart-container chart-container">
          <div className="monthly-loading">
            <div className="monthly-loading-spinner"></div>
            <div className="monthly-loading-text" style={{ fontSize: isLaptop ? '14px' : '16px' }}>
              {isFiltersApplying ? t('monthlyChart.filterApplying') : t('monthlyChart.loading')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="monthly-open-sales-chart">
        <div className="monthly-chart-container">
          <div className="monthly-error">
            <div className="monthly-error-icon">⚠️</div>
            <div className="monthly-error-text" style={{ fontSize: isLaptop ? '14px' : '16px' }}>
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (((chartData.monthly.length === 0 && viewMode === 'monthly') || 
       (chartData.quarterly.length === 0 && viewMode === 'quarterly')) && !loading) {
    return (
      <div className="monthly-open-sales-chart">
        <div className="monthly-chart-container">
          <div className="monthly-empty">
            <div className="monthly-empty-icon">📊</div>
            <div className="monthly-empty-text" style={{ fontSize: isLaptop ? '14px' : '16px' }}>
              {t('monthlyChart.noData')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="monthly-open-sales-chart">
      {isFiltersApplying && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <span style={{ fontSize: isLaptop ? '14px' : '16px' }}>
            {t('monthlyChart.filterApplying')}
          </span>
        </div>
      )}
      
      <div className="monthly-title-band">
        <h2 style={{
          fontSize: window.innerWidth <= 480 ? '16px' : (window.innerWidth <= 1024 ? '15px' : (isLaptop ? '16px' : '18px')),
          padding: isLaptop ? '8px 20px' : '10px 25px'
        }}>
          {currentTitle}
        </h2>
      </div>
      <div className="monthly-chart-container">
        <div className="monthly-navigation">
          <div className="monthly-nav-left">
            {}
            {(viewMode === 'daily' || viewMode === 'yearly' || viewMode === 'comparison' || 
              (isIPhone() && viewMode === 'monthly' && selectedQuarter)) && (
              <button 
                className="monthly-back-button" 
                onClick={handleBackToMonthly}
                disabled={isFiltersApplying}
                style={{
                  fontSize: isLaptop ? '11px' : '13px',
                  padding: isLaptop ? '4px 12px' : '5px 15px'
                }}
              >
                {(() => {
                  if (viewMode === 'yearly' || viewMode === 'comparison') {
                    return isIPhone() ? t('monthlyChart.backToQuarterly') : t('monthlyChart.backToMonthly');
                  }
                  if (isIPhone() && viewMode === 'monthly' && selectedQuarter) {
                    return t('monthlyChart.backToQuarterly');
                  }
                  return t('monthlyChart.backToMonthly');
                })()}
              </button>
            )}
            
            {}
            {(viewMode === 'monthly' || (isIPhone() && viewMode === 'quarterly')) && !(isIPhone() && viewMode === 'monthly' && selectedQuarter) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button 
                  className="monthly-yearly-button" 
                  onClick={handleShowYearlyView}
                  disabled={isFiltersApplying}
                  style={{
                    fontSize: isLaptop ? '11px' : '13px',
                    padding: isLaptop ? '4px 12px' : '5px 15px'
                  }}
                >
                  {t('monthlyChart.viewYearlyTrend')}
                </button>
                <button 
                  className="monthly-yearly-button" 
                  onClick={handleShowComparisonView}
                  disabled={isFiltersApplying}
                  style={{
                    fontSize: isLaptop ? '11px' : '13px',
                    padding: isLaptop ? '4px 12px' : '5px 15px'
                  }}
                >
                  {t('monthlyChart.viewMonthComparison')}
                </button>
              </div>
            )}
            
            {(viewMode !== 'monthly' && viewMode !== 'quarterly' && loading) && (
              <span className="monthly-additional-loading" style={{ fontSize: isLaptop ? '12px' : '14px' }}>
                {t('monthlyChart.loadingAdditional')}
              </span>
            )}
          </div>
          
          {}
          {((viewMode === 'quarterly' && chartData.quarterly.length > 0) || 
            (viewMode === 'monthly' && chartData.monthly.length > 0)) && 
            !(isIPhone() && viewMode === 'monthly' && selectedQuarter) && (
            <div className="monthly-click-hint" style={{ 
              fontSize: isLaptop ? '11px' : '13px'
            }}>
              {viewMode === 'quarterly' ? t('monthlyChart.quarterlyClickHint') : t('monthlyChart.clickHint')}
            </div>
          )}
        </div>
        
        {(viewMode !== 'monthly' && error) && (
          <div className="monthly-additional-error" style={{ fontSize: isLaptop ? '12px' : '14px' }}>
            ⚠️ {error}
          </div>
        )}
        
        <ReactECharts 
          option={currentOption} 
          style={{height: isLaptop ? '380px' : '460px', width: '100%'}}
          className="echarts-monthly-sales"
          onEvents={chartEvents}
          opts={{
            renderer: 'canvas',
            width: 'auto',
            height: 'auto'
          }}
        />
      </div>
    </div>
  );
};  

export default MonthlyOpenSalesChart;